<template>
    <div class="modal fade ix-dm-modal" tabindex="-1" aria-hidden="true">
        <div class="modal-dialog modal-lg">
            <div class="modal-content" v-if="mode">
                <div class="modal-header">
                    <h5 class="modal-title">
                        <template v-if="mode == 'upload'"><i class="far fa-cloud-upload mr-2"></i>Ajouter {{ files.length > 1 ? files.length + ' fichiers' : (files.length == 1 ? 'un fichier' : 'des fichiers') }}</template>
                        <template v-else-if="mode == 'folder'"><i class="far fa-folder-plus mr-2"></i>Nouveau dossier</template>
                        <template v-else-if="document.is_folder"><i class="far fa-folder mr-2"></i>Modifier le dossier</template>
                        <template v-else><i class="far fa-file mr-2"></i>Modifier le document</template>
                    </h5>
                    <button type="button" class="close" data-dismiss="modal" aria-label="Close">
                        <i class="fal fa-times"></i>
                    </button>
                </div>

                <div class="modal-body">
                    <!-- Fichiers à envoyer -->
                    <template v-if="mode == 'upload'">
                        <div class="ix-dm-file" v-for="(item, index) in files" :key="'file-' + index" :class="{ 'is-invalid': item.error }">
                            <div class="ix-dm-ico" :class="fileType(item.extension)"><i class="far" :class="fileIcon(item.extension)"></i></div>
                            <input type="text" v-model="item.name" class="ix-dm-file-name" placeholder="Nom du document">
                            <span class="text-danger ix-font-small" v-if="item.error">{{ item.error }}</span>
                            <span class="ix-dm-muted" v-else>.{{ item.extension }} · {{ item.size | formatFileSize(1) }}</span>
                            <button type="button" class="ix-dm-act" @click="files.splice(index, 1)" title="Retirer"><i class="far fa-trash"></i></button>
                        </div>

                        <div class="ix-dm-mini-drop" :class="{ 'is-dragging': isDragging, 'is-large': files.length == 0 }"
                            @dragenter.prevent="isDragging = true" @dragover.prevent="isDragging = true" @dragleave="isDragging = false"
                            @drop.prevent="onDrop" @click="$refs.fileInput.click()">
                            <i class="far fa-plus mr-1"></i>
                            <span class="ix-dm-desktop-only">{{ files.length ? "Déposez d'autres fichiers ou" : 'Déposez vos fichiers ou' }} <a>parcourez</a> · 10 Mo max par fichier</span>
                            <span class="ix-dm-mobile-only"><a>{{ files.length ? "Ajouter d'autres fichiers" : 'Choisir des fichiers' }}</a> · 10 Mo max</span>
                            <input type="file" multiple class="d-none" ref="fileInput" @change="onFilesPicked">
                        </div>
                    </template>

                    <!-- Nom du dossier ou du document -->
                    <div class="form-group" v-else>
                        <label for="documentName" class="required">Nom</label>
                        <input type="text" class="form-control" v-model="document.name" id="documentName" required @keyup.enter="submit()">
                    </div>

                    <div class="form-group" v-if="mode == 'edit' && document.is_folder">
                        <div class="custom-control custom-switch">
                            <input class="custom-control-input" type="checkbox" value="1" :id="'documentPermissions' + _uid" v-model="keepPermissions">
                            <label class="custom-control-label" :for="'documentPermissions' + _uid">Appliquer ses droits d'accès à l'ensemble des éléments de ce dossier</label>
                        </div>
                    </div>

                    <!-- Rôles ayant accès -->
                    <div class="d-flex justify-content-between align-items-center mb-2">
                        <span class="ix-text-bold">Accès</span>
                        <a class="ix-font-small ix-dm-link" @click="toggleAllRoles()" v-if="roles.length">{{ allRolesChecked ? 'Tout désélectionner' : 'Tout sélectionner' }}</a>
                    </div>
                    <div v-if="loadingRoles" class="ix-dm-muted"><i class="far fa-circle-notch fa-spin"></i> Chargement des rôles</div>
                    <div>
                        <button type="button" v-for="role in roles" :key="'role-' + role.id" class="ix-dm-chip" :class="{ on: role.is_checked }"
                            @click="role.is_checked = role.is_checked ? 0 : 1">
                            <i class="fas fa-check" v-if="role.is_checked"></i> {{ role.name }}
                        </button>
                    </div>

                    <div class="text-danger mt-2" v-if="errorMessage">{{ errorMessage }}</div>
                </div>

                <div class="modal-footer justify-content-between">
                    <span class="ix-dm-muted">Destination : {{ destinationLabel }}</span>
                    <div>
                        <button type="button" class="ix-btn" data-dismiss="modal">Annuler</button>
                        <button type="button" class="ix-btn green" :disabled="!canSubmit || loadingForm" @click="submit()">
                            <span v-if="loadingForm"><i class="far fa-circle-notch fa-spin"></i></span>
                            <span v-else><i class="far fa-check"></i> {{ submitLabel }}</span>
                        </button>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import $ from "jquery";
import { fileType, fileIcon } from "./documentFileTypes"

// Limite du serveur : max:10000 (Ko)
const MAX_FILE_SIZE = 10000 * 1024;

export default {
    name: "DocumentsManagerModal",
    props: {
        admin: Number,
        url: String,
        rolesUrl: String,
        documentableType: String,
        documentableId: Number,
        node: Object,
        breadcrumbs: { type: Array, default: () => [] },
    },
    data() {
        return {
            // upload : envoi de fichiers, folder : nouveau dossier, edit : modification
            mode: null,
            files: [],
            folder: null,
            document: null,
            roles: [],
            keepPermissions: 1,
            loadingRoles: false,
            loadingForm: false,
            isDragging: false,
            errorMessage: null,
        }
    },
    computed: {
        validFiles() {
            return this.files.filter(item => !item.error && item.name);
        },
        canSubmit() {
            if (this.mode == 'upload') return this.validFiles.length > 0;
            return this.document && this.document.name;
        },
        submitLabel() {
            if (this.mode == 'upload') {
                const count = this.validFiles.length;
                return 'Envoyer ' + (count > 1 ? count + ' fichiers' : 'le fichier');
            }
            return this.mode == 'folder' ? 'Créer' : 'Enregistrer';
        },
        allRolesChecked() {
            return this.roles.length > 0 && this.roles.every(role => role.is_checked);
        },
        destinationLabel() {
            const path = ['Documents'].concat(this.breadcrumbs.map(item => item.name));
            if (this.node) path.push(this.node.name);
            if (this.folder) path.push(this.folder.name);
            return path.join(' / ');
        },
    },
    methods: {
        fileType,
        fileIcon,
        show() {
            $(this.$el).modal('show');
        },
        hide() {
            $(this.$el).modal('hide');
        },
        reset(mode) {
            this.mode = mode;
            this.files = [];
            this.folder = null;
            this.errorMessage = null;
            this.keepPermissions = 1;
            this.document = { id: null, name: null, is_folder: mode == 'folder' ? 1 : 0 };
        },
        // Droits proposés : ceux du dossier de destination, sinon tous les rôles
        applyDefaultRoles(folder) {
            const source = folder || this.node;
            if (source && source.roles) {
                this.checkRolesFromDocument(source);
            } else {
                this.roles.forEach(role => { role.is_checked = 1; });
            }
        },
        /** Ancienne entrée : true = nouveau dossier, false = envoi de fichiers */
        startDocumentModal(isFolder) {
            if (isFolder) {
                this.reset('folder');
                this.applyDefaultRoles(null);
                this.show();
            } else {
                this.startUpload([], null);
            }
        },
        startUpload(files, folder) {
            this.reset('upload');
            this.folder = folder;
            this.addFiles(files);
            this.applyDefaultRoles(folder);
            this.show();
        },
        editDocument(document) {
            this.reset('edit');
            this.document = {
                id: document.id,
                name: document.name,
                is_folder: document.is_folder ? 1 : 0,
                document_id: document.document_id,
            };
            this.checkRolesFromDocument(document);
            this.show();
        },
        addFiles(files) {
            files.forEach(file => {
                const dot = file.name.lastIndexOf('.');
                this.files.push({
                    file: file,
                    name: dot > 0 ? file.name.substring(0, dot) : file.name,
                    extension: dot > 0 ? file.name.substring(dot + 1) : '',
                    size: file.size,
                    error: file.size > MAX_FILE_SIZE ? 'Dépasse 10 Mo' : null,
                });
            });
        },
        onFilesPicked(e) {
            this.addFiles(Array.from(e.target.files || []));
            e.target.value = '';
        },
        onDrop(e) {
            this.isDragging = false;
            this.addFiles(Array.from(e.dataTransfer.files || []));
        },
        toggleAllRoles() {
            const value = this.allRolesChecked ? 0 : 1;
            this.roles.forEach(role => { role.is_checked = value; });
        },
        checkRolesFromDocument(document) {
            const checkedRoles = (document.roles || []).map(role => role.id);
            this.roles.forEach(role => {
                role.is_checked = checkedRoles.includes(role.id) ? 1 : 0;
            });
        },
        checkedRoles() {
            return this.roles.filter(role => role.is_checked).map(role => role.id).join(',');
        },
        async getRoles() {
            this.loadingRoles = true;
            fetch(this.rolesUrl + "?orderBy=!name", {
                method: 'GET',
                ...this.getHttpJsonRequestHeaders()
            }).then((response) => {
                this.loadingRoles = false;
                if (response.status == 200) {
                    response.json().then((data) => {
                        // is_checked posé avant l'affectation pour être réactif
                        this.roles = data.map(role => ({ ...role, is_checked: 1 }));
                    });
                }
            }).catch(error => {
                this.loadingRoles = false;
                console.log(error);
            });
        },
        submit() {
            if (!this.canSubmit || this.loadingForm) return;

            // Les fichiers sont envoyés par le gestionnaire (progression affichée dans la liste)
            if (this.mode == 'upload') {
                this.$emit('upload', {
                    files: this.validFiles,
                    roles: this.checkedRoles(),
                    folder: this.folder,
                });
                this.hide();
                return;
            }

            this.saveDocument();
        },
        saveDocument() {
            const data = new FormData();
            data.append('name', this.document.name);
            data.append('documentable_id', this.documentableId);
            data.append('documentable_type', this.documentableType);
            data.append('is_folder', this.document.is_folder ? 1 : 0);
            data.append('roles', this.checkedRoles());

            if (this.document.id) {
                data.append('_method', 'PUT');
                data.append('document_id', this.document.document_id || '');
                if (this.document.is_folder) {
                    data.append('keep_permissions', this.keepPermissions ? 1 : 0);
                }
            } else {
                data.append('document_id', this.node ? this.node.id : '');
            }

            this.loadingForm = true;
            fetch(this.url + (this.document.id ? '/' + this.document.id : ''), {
                method: "POST",
                ...this.getHttpJsonRequestHeaders(true),
                body: data
            }).then((response) => {
                this.loadingForm = false;
                if (response.ok) {
                    this.errorMessage = null;
                    this.hide();
                    this.$emit('saved');
                } else {
                    this.errorMessage = "Impossible d'enregistrer.";
                }
            }).catch(error => {
                this.loadingForm = false;
                this.errorMessage = "Impossible d'enregistrer.";
                console.log(error);
            });
        },
    },

    mounted() {
        this.getRoles();
    }
}
</script>
